<template>
    <vx-expandable-card>
        <template v-slot:header>
            Address
        </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">Address Line 1</vx-label>

                    <vx-input
                        data-test="address-input"
                        v-model="group.address"
                        size="lg"
                        :error="props.errors.address"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md" class="w-full">
                        <div class="flex items-center justify-between flex-grow">
                            <span>Address Line 2</span>
                            <vx-badge color="slate" size="xs">
                                <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                            </vx-badge>
                        </div>
                    </vx-label>

                    <vx-input
                        data-test="address-line-2-input"
                        v-model="group.address_line_2"
                        size="lg"
                        :error="props.errors.address_line_2"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Country</vx-label>
                    <vx-selectable
                        data-test="country-select"
                        :modelValue="group.country"
                        @update:modelValue="onCountryChange"
                        :options="countries"
                        :reduce="(option) => option.value"
                        size="lg"
                        :clearable="true"
                        :error="props.errors.country"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">City</vx-label>

                    <vx-input
                        data-test="city-input"
                        v-model="group.city"
                        size="lg"
                        :error="props.errors.city"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">State</vx-label>

                    <vx-selectable
                        v-if="['US', 'CA'].includes(group.country)"
                        data-test="state-select"
                        v-model="group.state"
                        :options="states[group.country] ?? []"
                        :reduce="(option) => option.value"
                        size="lg"
                        :clearable="true"
                        :error="props.errors.state"
                    ></vx-selectable>
                    <vx-input
                        v-else
                        data-test="state-input"
                        v-model="group.state"
                        size="lg"
                        :error="props.errors.state"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Zip</vx-label>

                    <vx-input
                        data-test="zip-input"
                        v-model="group.zip"
                        size="lg"
                        :error="props.errors.zip"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Time-zone</vx-label>

                    <vx-selectable
                        data-test="time-zone-select"
                        v-model="timeZone"
                        :options="timeZonesList"
                        size="lg"
                        :reduce="option => option.value"
                        placeholder="Select a time-zone"
                        :clearable="true"
                        :error="props.timeZoneError"
                    ></vx-selectable>

                    <div class="mt-2">
                        <vx-button
                            data-test="derive-timezone-button"
                            @click="deriveNow"
                            size="sm"
                            color="muted"
                        >
                            Derive timezone from address
                        </vx-button>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxBadge, VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { computed } from 'vue';
import { countries, states } from '~/constants/countries';
import timeZones from '~/utils/timeZones';
import { timezoneFromZipCode, timezoneFromCanadianProvince } from '~/utils/zipToTimezone';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();

const props = defineProps({
    errors: {
        type: Object,
        default: () => ({}),
    },
    timeZoneError: {
        type: [String, Array],
    },
});

const group = defineModel({
    required: true,
    type: Object,
});

const timeZone = defineModel('timeZone', {
    type: String,
    default: null,
});

const timeZonesList = computed(() => timeZones.map(timeZone => ({
    value: timeZone,
    label: timeZone.split('_').join(' '),
})));

const getDerivedTimezone = () => {
    const { country, state, zip } = group.value;

    if ('US' === country && zip && 5 === zip.length) {
        return timezoneFromZipCode(zip);
    }

    if ('CA' === country && state) {
        return timezoneFromCanadianProvince(state);
    }

    return null;
};

const deriveNow = () => {
    const tz = getDerivedTimezone();
    if (tz === null) {
        toasted.error('Could not derive timezone. Set country and a valid ZIP (US) or state (Canada).');
        return;
    }
    timeZone.value = tz;
};

const onCountryChange = (value) => {
    group.value.country = value;
    group.value.city = null;
    group.value.state = null;
    group.value.zip = null;
};
</script>
